/**
 * Transcribe Streaming Service
 * Manages browser-direct connection to AWS Transcribe Streaming.
 * Uses Cognito Identity Pool credentials from awsClients.ts.
 *
 * Accepts PCM audio chunks (16kHz 16-bit LE) and yields transcript events.
 */

import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
  AudioStream,
  LanguageCode,
} from '@aws-sdk/client-transcribe-streaming';
import { getCredentialsProvider } from '@/lib/awsClients';

const REGION = import.meta.env.REACT_APP_AWS_REGION || 'eu-west-2';
const SAMPLE_RATE = 16000;

export interface TranscriptFragment {
  index: number;
  text: string;
  startTime: number;
  endTime: number;
  isFinal: boolean;
  speaker?: string;
}

export interface TranscribeStreamingCallbacks {
  onTranscript: (fragment: TranscriptFragment) => void;
  onError: (error: Error) => void;
  onClose: () => void;
}

/**
 * Creates an async iterable of AudioStream events from a queue of PCM chunks.
 */
function createAudioStream(
  getNextChunk: () => Promise<Uint8Array | null>
): AsyncIterable<AudioStream> {
  return {
    [Symbol.asyncIterator]() {
      return {
        async next(): Promise<IteratorResult<AudioStream>> {
          const chunk = await getNextChunk();
          if (chunk === null) {
            return { done: true, value: undefined };
          }
          return {
            done: false,
            value: { AudioEvent: { AudioChunk: chunk } },
          };
        },
      };
    },
  };
}

export class TranscribeStreamingSession {
  private client: TranscribeStreamingClient | null = null;
  private audioQueue: Uint8Array[] = [];
  private audioResolve: (() => void) | null = null;
  private stopped = false;
  private fragmentIndex = 0;
  private callbacks: TranscribeStreamingCallbacks;
  private languageCode: string;

  constructor(languageCode: string, callbacks: TranscribeStreamingCallbacks) {
    this.languageCode = languageCode;
    this.callbacks = callbacks;
  }

  /**
   * Start the Transcribe Streaming session.
   * Must be called before pushAudio().
   */
  async start(): Promise<void> {
    const credentials = await getCredentialsProvider();

    this.client = new TranscribeStreamingClient({
      region: REGION,
      credentials,
    });

    this.stopped = false;
    this.fragmentIndex = 0;
    this.audioQueue = [];

    const getNextChunk = (): Promise<Uint8Array | null> => {
      return new Promise((resolve) => {
        if (this.stopped) {
          resolve(null);
          return;
        }
        if (this.audioQueue.length > 0) {
          resolve(this.audioQueue.shift()!);
          return;
        }
        // Wait for next chunk
        this.audioResolve = () => {
          if (this.stopped) {
            resolve(null);
          } else if (this.audioQueue.length > 0) {
            resolve(this.audioQueue.shift()!);
          } else {
            resolve(null);
          }
        };
      });
    };

    const audioStream = createAudioStream(getNextChunk);

    const command = new StartStreamTranscriptionCommand({
      LanguageCode: this.languageCode as LanguageCode,
      MediaEncoding: 'pcm',
      MediaSampleRateHertz: SAMPLE_RATE,
      AudioStream: audioStream,
      EnablePartialResultsStabilization: true,
      PartialResultsStability: 'medium',
    });

    try {
      const response = await this.client.send(command);

      if (response.TranscriptResultStream) {
        for await (const event of response.TranscriptResultStream) {
          if (this.stopped) break;

          if (event.TranscriptEvent?.Transcript?.Results) {
            for (const result of event.TranscriptEvent.Transcript.Results) {
              if (result.Alternatives && result.Alternatives.length > 0) {
                const alt = result.Alternatives[0];
                const fragment: TranscriptFragment = {
                  index: this.fragmentIndex++,
                  text: alt.Transcript || '',
                  startTime: result.StartTime || 0,
                  endTime: result.EndTime || 0,
                  isFinal: !result.IsPartial,
                };
                this.callbacks.onTranscript(fragment);
              }
            }
          }
        }
      }

      this.callbacks.onClose();
    } catch (error) {
      if (!this.stopped) {
        this.callbacks.onError(error instanceof Error ? error : new Error(String(error)));
      }
    }
  }

  /**
   * Push a PCM audio chunk to the Transcribe stream.
   */
  pushAudio(chunk: Uint8Array): void {
    if (this.stopped) return;
    this.audioQueue.push(chunk);
    if (this.audioResolve) {
      const resolve = this.audioResolve;
      this.audioResolve = null;
      resolve();
    }
  }

  /**
   * Stop the Transcribe Streaming session.
   */
  stop(): void {
    this.stopped = true;
    // Resolve any pending audio wait
    if (this.audioResolve) {
      const resolve = this.audioResolve;
      this.audioResolve = null;
      resolve();
    }
    if (this.client) {
      this.client.destroy();
      this.client = null;
    }
  }
}
